import { observer } from 'mobx-react-lite';
import { subjectDetailsLabel } from 'owa-locstrings/lib/strings/subjectdetailslabel.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47742 */
import * as React from 'react';
import CharmSuggestions from './CharmSuggestions';
import { getComposeEvent, getComposeViewState } from 'owa-calendar-compose-store-manager';
import type TitleAndSuggestionsProps from './TitleAndSuggestionsProps';
import { ThemeProvider } from '@fluentui/react/lib/utilities/ThemeProvider';
import { ITextField, TextField } from '@fluentui/react/lib/TextField';
import { onSubjectChanged } from 'owa-calendar-compose-form-actions';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { LazyAnnounced } from 'owa-controls-announced';
import { titleSuggestionsAnnouncementText } from './TitleAndSuggestions.locstring.json';

import styles from './TitleAndSuggestions.scss';
import classnamesBind from 'classnames/bind';
let classNames = classnamesBind.bind(styles);

const MAX_TITLE_LENGTH = 255;

export default observer(function TitleAndSuggestions(props: TitleAndSuggestionsProps) {
    const rootElement = React.useRef<HTMLDivElement>();
    const titleElement = React.useRef<ITextField>();
    React.useEffect(() => {
        const { selectionStart, selectionEnd } = titleElement.current;
        const { updateSelectionRange, shouldUpdateSelectionRange } = props;
        if (shouldUpdateSelectionRange && updateSelectionRange) {
            updateSelectionRange(selectionStart, selectionEnd);
        }
    });

    const onChange = React.useCallback(
        (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, subject: string) => {
            onSubjectChanged(event, subject, props.formId);
        },
        [props.formId]
    );

    const onSelect = () => {
        const { onSelectText } = props;
        if (onSelectText) {
            const { selectionStart, selectionEnd } = titleElement.current;
            onSelectText(selectionStart, selectionEnd);
        }
    };
    let {
        className,
        colorScheme,
        inputStyle,
        autoFocus,
        placeholder,
        onKeyDown,
        onFocus,
        onBlur,
        onSelectCharmSuggestion,
        charmSuggestionsClassName,
        charmSuggestionsTarget,
        disabled,
        formId,
    } = props;
    let title = getComposeEvent(formId).Subject;
    const charmSuggestionsViewState = getComposeViewState(formId).charmSuggestionsViewState;
    const showSuggestions = charmSuggestionsViewState.shouldShowSuggestions;

    const calendarTheme = themeFromColorScheme(colorScheme);
    const inputFocusBorderColor = colorScheme.accessibleText.msColorWhiteBackground;
    const calendarThemeWithSemanticColors = {
        ...calendarTheme,
        semanticColors: {
            inputBorderHovered: calendarTheme.palette.neutralDark,
            inputBorder: calendarTheme.palette.neutralTertiary,
            inputFocusBorderAlt: inputFocusBorderColor,
        },
    };
    const textFieldWrapperStyles = {
        wrapper: {
            '&:hover::after': {
                borderBottom: '1px solid !important',
            },
            '&::after': {
                borderBottom: '1px solid ' + inputFocusBorderColor + ' !important',
            },
        },
    };
    return (
        <div
            className={classNames(className, styles.container)}
            ref={ref => (rootElement.current = ref)}>
            <ThemeProvider theme={calendarThemeWithSemanticColors}>
                <TextField
                    onChange={onChange}
                    componentRef={(ref: ITextField) => (titleElement.current = ref)}
                    styles={textFieldWrapperStyles}
                    style={inputStyle}
                    borderless={true}
                    value={title ? title : ''}
                    ariaLabel={loc(subjectDetailsLabel)}
                    autoFocus={autoFocus}
                    placeholder={placeholder}
                    onKeyDown={onKeyDown}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onSelect={onSelect}
                    underlined={true}
                    maxLength={MAX_TITLE_LENGTH}
                    disabled={disabled}
                />
            </ThemeProvider>
            {showSuggestions && (
                <>
                    <CharmSuggestions
                        viewState={charmSuggestionsViewState}
                        onSelectIcon={onSelectCharmSuggestion}
                        subject={title}
                        colorScheme={colorScheme}
                        containerClass={charmSuggestionsClassName}
                        target={
                            charmSuggestionsTarget ? charmSuggestionsTarget : rootElement.current
                        }
                        formId={formId}
                    />
                    <LazyAnnounced message={loc(titleSuggestionsAnnouncementText)} />
                </>
            )}
        </div>
    );
});
